import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";
import {
  Plus,
  Upload,
  LayoutGrid,
  List,
  Columns3,
  ChevronDown,
  X,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

interface Deal {
  id: string;
  name: string;
  stage: string;
  pipelineType: string;
  valueUsd: number;
  probability: number;
  entity: string;
  leadSource?: string;
  type?: string;
  territory?: string;
  closeDate?: string;
  account?: { id: string; name: string };
  owner?: { id: string; name: string };
}

type ViewMode = "kanban" | "list" | "grid";

// ─── Constants ───────────────────────────────────────────────────────────────

const PIPELINES = [
  { key: "CONSULTING", label: "Consulting" },
  { key: "PRODUCT",    label: "Products" },
  { key: "STAFFING",   label: "Staffing" },
  { key: "GOVERNMENT", label: "Government" },
] as const;

type PipelineKey = typeof PIPELINES[number]["key"];

const STAGE_BY_PIPELINE: Record<PipelineKey, string[]> = {
  CONSULTING:  ["QUALIFYING","DISCOVERY","PROPOSAL","NEGOTIATION","CLOSED_WON","CLOSED_LOST"],
  PRODUCT:     ["QUALIFYING","DISCOVERY","PROPOSAL","NEGOTIATION","CLOSED_WON","CLOSED_LOST"],
  STAFFING:    ["REQUISITION","CANDIDATE_SOURCING","SUBMITTED_TO_CLIENT","INTERVIEW","OFFER","PLACED","CLOSED_LOST"],
  GOVERNMENT:  ["MARKET_RESEARCH","CAPTURE","PROPOSAL_DEV","SUBMITTED","AWARDED","LOST"],
};

const STAGE_COLOR: Record<string, string> = {
  QUALIFYING: "bg-slate-100 text-slate-700",
  DISCOVERY: "bg-blue-100 text-blue-700",
  PROPOSAL: "bg-purple-100 text-purple-700",
  NEGOTIATION: "bg-amber-100 text-amber-700",
  CLOSED_WON: "bg-green-100 text-green-700",
  CLOSED_LOST: "bg-red-100 text-red-700",
  REQUISITION: "bg-slate-100 text-slate-700",
  CANDIDATE_SOURCING: "bg-blue-100 text-blue-700",
  SUBMITTED_TO_CLIENT: "bg-indigo-100 text-indigo-700",
  INTERVIEW: "bg-purple-100 text-purple-700",
  OFFER: "bg-amber-100 text-amber-700",
  PLACED: "bg-green-100 text-green-700",
  MARKET_RESEARCH: "bg-slate-100 text-slate-700",
  CAPTURE: "bg-blue-100 text-blue-700",
  PROPOSAL_DEV: "bg-purple-100 text-purple-700",
  SUBMITTED: "bg-indigo-100 text-indigo-700",
  AWARDED: "bg-green-100 text-green-700",
  LOST: "bg-red-100 text-red-700",
};

const VALUE_RANGES = [
  { label: "Any size", min: 0, max: Infinity },
  { label: "< $10k",  min: 0, max: 10000 },
  { label: "$10k – $100k", min: 10000, max: 100000 },
  { label: "$100k – $1M",  min: 100000, max: 1000000 },
  { label: "> $1M",   min: 1000000, max: Infinity },
];

const ENTITY_OPTIONS = ["US", "INDIA", "BOTH"];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtUsd(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n.toLocaleString()}`;
}

function stageLabel(s: string) {
  return s.replace(/_/g, " ");
}

// ─── Small sub-components ────────────────────────────────────────────────────

function DealCard({ deal, compact = false }: { deal: Deal; compact?: boolean }) {
  const stageCls = STAGE_COLOR[deal.stage] ?? "bg-gray-100 text-gray-700";
  return (
    <Card className={cn("text-sm transition-colors hover:bg-muted/40 cursor-grab active:cursor-grabbing", compact ? "p-3" : "p-4")}>
      <div className="font-medium truncate">{deal.name}</div>
      <div className="text-xs text-muted-foreground truncate">{deal.account?.name ?? "—"}</div>
      <div className="mt-2 flex items-center justify-between gap-1 text-xs">
        <span className="tabular-nums font-medium">{fmtUsd(Number(deal.valueUsd))}</span>
        <span className="text-muted-foreground">{deal.probability}%</span>
      </div>
      {!compact && (
        <div className="mt-2 flex flex-wrap gap-1">
          <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-medium", stageCls)}>
            {stageLabel(deal.stage)}
          </span>
          {deal.owner?.name && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
              {deal.owner.name}
            </span>
          )}
        </div>
      )}
    </Card>
  );
}

// ─── Draggable card (wraps DealCard) ────────────────────────────────────────

function DraggableDealCard({ deal }: { deal: Deal }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: deal.id, data: { deal } });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{ opacity: isDragging ? 0.4 : 1 }}
    >
      <Link to={`/deals/${deal.id}`} onClick={(e) => { if (isDragging) e.preventDefault(); }}>
        <DealCard deal={deal} compact />
      </Link>
    </div>
  );
}

// ─── Droppable column ────────────────────────────────────────────────────────

function KanbanColumn({ stage, deals }: { stage: string; deals: Deal[] }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  return (
    <div className="flex min-w-[260px] flex-1 flex-col">
      <div className="mb-2 flex items-center justify-between px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <span>{stageLabel(stage)}</span>
        <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px]">{deals.length}</span>
      </div>
      <div
        ref={setNodeRef}
        className={cn(
          "flex-1 min-h-[120px] rounded-lg p-1.5 space-y-2 transition-colors",
          isOver ? "bg-primary/5 ring-2 ring-primary/20" : "bg-muted/30",
        )}
      >
        {deals.map((d) => (
          <DraggableDealCard key={d.id} deal={d} />
        ))}
        {deals.length === 0 && (
          <p className="py-6 text-center text-xs text-muted-foreground/60">Drop here</p>
        )}
      </div>
    </div>
  );
}

// ─── Filter bar ──────────────────────────────────────────────────────────────

interface Filters {
  ownerId: string;
  valueRange: number; // index into VALUE_RANGES
  entity: string;
  leadSource: string;
  stage: string;
}

function FilterBar({
  filters,
  onChange,
  owners,
  leadSources,
  stages,
}: {
  filters: Filters;
  onChange: (f: Filters) => void;
  owners: { id: string; name: string }[];
  leadSources: string[];
  stages: string[];
}) {
  const active =
    filters.ownerId !== "" ||
    filters.valueRange !== 0 ||
    filters.entity !== "" ||
    filters.leadSource !== "" ||
    filters.stage !== "";

  function set<K extends keyof Filters>(k: K, v: Filters[K]) {
    onChange({ ...filters, [k]: v });
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Owner */}
      <select
        className="h-8 rounded-md border bg-background px-2 text-xs focus:outline-none"
        value={filters.ownerId}
        onChange={(e) => set("ownerId", e.target.value)}
      >
        <option value="">All owners</option>
        {owners.map((u) => (
          <option key={u.id} value={u.id}>{u.name}</option>
        ))}
      </select>

      {/* Value / Size */}
      <select
        className="h-8 rounded-md border bg-background px-2 text-xs focus:outline-none"
        value={filters.valueRange}
        onChange={(e) => set("valueRange", Number(e.target.value))}
      >
        {VALUE_RANGES.map((r, i) => (
          <option key={i} value={i}>{r.label}</option>
        ))}
      </select>

      {/* Entity / Region */}
      <select
        className="h-8 rounded-md border bg-background px-2 text-xs focus:outline-none"
        value={filters.entity}
        onChange={(e) => set("entity", e.target.value)}
      >
        <option value="">All regions</option>
        {ENTITY_OPTIONS.map((e) => (
          <option key={e} value={e}>{e}</option>
        ))}
      </select>

      {/* Lead source */}
      {leadSources.length > 0 && (
        <select
          className="h-8 rounded-md border bg-background px-2 text-xs focus:outline-none"
          value={filters.leadSource}
          onChange={(e) => set("leadSource", e.target.value)}
        >
          <option value="">All sources</option>
          {leadSources.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      )}

      {/* Stage (list/grid only) */}
      {stages.length > 0 && (
        <select
          className="h-8 rounded-md border bg-background px-2 text-xs focus:outline-none"
          value={filters.stage}
          onChange={(e) => set("stage", e.target.value)}
        >
          <option value="">All stages</option>
          {stages.map((s) => (
            <option key={s} value={s}>{stageLabel(s)}</option>
          ))}
        </select>
      )}

      {active && (
        <button
          onClick={() => onChange({ ownerId: "", valueRange: 0, entity: "", leadSource: "", stage: "" })}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <X className="h-3 w-3" /> Clear
        </button>
      )}
    </div>
  );
}

// ─── List view ───────────────────────────────────────────────────────────────

function ListView({ deals }: { deals: Deal[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full text-sm">
        <thead className="border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            {["Deal Name","Account","Stage","Owner","Value","Probability","Entity","Source","Close Date"].map((h) => (
              <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {deals.length === 0 && (
            <tr>
              <td colSpan={9} className="py-10 text-center text-muted-foreground text-xs">No deals match the current filters.</td>
            </tr>
          )}
          {deals.map((d) => {
            const stageCls = STAGE_COLOR[d.stage] ?? "bg-gray-100 text-gray-700";
            return (
              <tr key={d.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                <td className="px-3 py-2 font-medium">
                  <Link to={`/deals/${d.id}`} className="hover:underline">{d.name}</Link>
                </td>
                <td className="px-3 py-2 text-muted-foreground">{d.account?.name ?? "—"}</td>
                <td className="px-3 py-2">
                  <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-medium", stageCls)}>
                    {stageLabel(d.stage)}
                  </span>
                </td>
                <td className="px-3 py-2 text-muted-foreground">{d.owner?.name ?? "—"}</td>
                <td className="px-3 py-2 tabular-nums font-medium">{fmtUsd(Number(d.valueUsd))}</td>
                <td className="px-3 py-2 text-muted-foreground">{d.probability}%</td>
                <td className="px-3 py-2 text-muted-foreground">{d.entity}</td>
                <td className="px-3 py-2 text-muted-foreground">{d.leadSource ?? "—"}</td>
                <td className="px-3 py-2 text-muted-foreground">
                  {d.closeDate ? new Date(d.closeDate).toLocaleDateString() : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Grid view ───────────────────────────────────────────────────────────────

function GridView({ deals }: { deals: Deal[] }) {
  if (deals.length === 0) {
    return <p className="text-sm text-muted-foreground">No deals match the current filters.</p>;
  }
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {deals.map((d) => (
        <Link key={d.id} to={`/deals/${d.id}`}>
          <DealCard deal={d} />
        </Link>
      ))}
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function DealsPage() {
  const [pipeline, setPipeline] = useState<PipelineKey>("CONSULTING");
  const [view, setView] = useState<ViewMode>("kanban");
  const [activeId, setActiveId] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>({
    ownerId: "",
    valueRange: 0,
    entity: "",
    leadSource: "",
    stage: "",
  });

  const queryClient = useQueryClient();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  // Fetch kanban data (for kanban view)
  const { data: kanbanData } = useQuery({
    queryKey: ["deals-kanban", pipeline],
    queryFn: async () => (await api.get(`/deals/kanban/${pipeline}`)).data,
  });

  // Fetch flat list data (for list + grid views)
  const { data: listData } = useQuery({
    queryKey: ["deals-list", pipeline],
    queryFn: async () => {
      const res = await api.get("/deals", { params: { pipelineType: pipeline, limit: 500 } });
      return res.data.data as Deal[];
    },
  });

  // Fetch users for owner filter
  const { data: usersData } = useQuery({
    queryKey: ["users-slim"],
    queryFn: async () => {
      const res = await api.get("/users", { params: { limit: 200 } });
      return (res.data.data ?? []) as { id: string; name: string }[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const owners = usersData ?? [];

  // Derive unique lead sources from current data
  const allDeals: Deal[] = useMemo(() => {
    if (view === "kanban") {
      const stages = kanbanData?.stages ?? {};
      return Object.values(stages).flat() as Deal[];
    }
    return listData ?? [];
  }, [view, kanbanData, listData]);

  const leadSources = useMemo(
    () => [...new Set(allDeals.map((d) => d.leadSource).filter(Boolean) as string[])].sort(),
    [allDeals],
  );

  const stages = STAGE_BY_PIPELINE[pipeline];

  // Apply filters to any deal array
  function applyFilters(deals: Deal[]) {
    const range = VALUE_RANGES[filters.valueRange];
    return deals.filter((d) => {
      if (filters.ownerId && d.owner?.id !== filters.ownerId) return false;
      const v = Number(d.valueUsd);
      if (v < range.min || v >= range.max) return false;
      if (filters.entity && d.entity !== filters.entity) return false;
      if (filters.leadSource && d.leadSource !== filters.leadSource) return false;
      if (filters.stage && d.stage !== filters.stage) return false;
      return true;
    });
  }

  // Kanban: grouped stages with filters applied
  const kanbanStages = useMemo(() => {
    const raw = kanbanData?.stages ?? {};
    const result: Record<string, Deal[]> = {};
    for (const stage of stages) {
      result[stage] = applyFilters((raw[stage] ?? []) as Deal[]);
    }
    return result;
  }, [kanbanData, filters, pipeline]);

  // List/Grid: flat filtered list
  const filteredList = useMemo(() => applyFilters(listData ?? []), [listData, filters]);

  // Stage update mutation
  const stageMutation = useMutation({
    mutationFn: async ({ id, stage }: { id: string; stage: string }) => {
      await api.put(`/deals/${id}`, { stage });
    },
    onMutate: async ({ id, stage }) => {
      await queryClient.cancelQueries({ queryKey: ["deals-kanban", pipeline] });
      const prev = queryClient.getQueryData(["deals-kanban", pipeline]);
      queryClient.setQueryData(["deals-kanban", pipeline], (old: any) => {
        if (!old) return old;
        const stages: Record<string, Deal[]> = {};
        let movedDeal: Deal | undefined;
        for (const [s, deals] of Object.entries(old.stages as Record<string, Deal[]>)) {
          stages[s] = deals.filter((d) => {
            if (d.id === id) { movedDeal = { ...d, stage }; return false; }
            return true;
          });
        }
        if (movedDeal) {
          stages[stage] = [movedDeal, ...(stages[stage] ?? [])];
        }
        return { ...old, stages };
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["deals-kanban", pipeline], ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["deals-kanban", pipeline] });
    },
  });

  // Drag handlers
  function handleDragStart(e: DragStartEvent) {
    setActiveId(e.active.id as string);
  }

  function handleDragEnd(e: DragEndEvent) {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;
    const deal: Deal = active.data.current?.deal;
    const newStage = over.id as string;
    if (deal && newStage !== deal.stage) {
      stageMutation.mutate({ id: deal.id, stage: newStage });
    }
  }

  const activeDeal = activeId
    ? (Object.values(kanbanData?.stages ?? {}).flat() as Deal[]).find((d) => d.id === activeId)
    : null;

  return (
    <>
      <PageHeader
        title="Deals"
        description="Track and manage deals across pipelines."
        actions={
          <>
            <Button asChild variant="outline">
              <Link to="/deals/import"><Upload className="mr-1 h-4 w-4" />Import CSV</Link>
            </Button>
            <Button asChild>
              <Link to="/deals/new"><Plus className="mr-1 h-4 w-4" />New Deal</Link>
            </Button>
          </>
        }
      />

      <div className="p-6 space-y-4">
        {/* Pipeline tabs + view toggle */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-2">
            {PIPELINES.map((p) => (
              <button
                key={p.key}
                onClick={() => setPipeline(p.key)}
                className={cn(
                  "rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
                  pipeline === p.key
                    ? "border-primary bg-primary/10 text-primary"
                    : "hover:bg-muted",
                )}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1 rounded-md border p-0.5">
            {([ ["kanban", Columns3], ["list", List], ["grid", LayoutGrid] ] as const).map(([v, Icon]) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={cn(
                  "rounded px-2.5 py-1.5 transition-colors",
                  view === v ? "bg-primary text-primary-foreground" : "hover:bg-muted",
                )}
                title={v.charAt(0).toUpperCase() + v.slice(1) + " view"}
              >
                <Icon className="h-4 w-4" />
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        <FilterBar
          filters={filters}
          onChange={setFilters}
          owners={owners}
          leadSources={leadSources}
          stages={view !== "kanban" ? stages : []}
        />

        {/* Views */}
        {view === "kanban" && (
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="flex gap-3 overflow-x-auto pb-4">
              {stages.map((stage) => (
                <KanbanColumn key={stage} stage={stage} deals={kanbanStages[stage] ?? []} />
              ))}
            </div>

            <DragOverlay>
              {activeDeal ? <DealCard deal={activeDeal} compact /> : null}
            </DragOverlay>
          </DndContext>
        )}

        {view === "list" && <ListView deals={filteredList} />}
        {view === "grid" && <GridView deals={filteredList} />}
      </div>
    </>
  );
}
